// src/components/Seo.tsx
// Shared SEO helper: per-page <title>/meta description, canonical URL,
// Open Graph + Twitter Card tags, and optional JSON-LD structured data.
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://www.dtmertsakarya.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/og-image.jpg`;

interface SeoProps {
  title: string;
  description: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const toAbsolute = (src: string) => (src.startsWith("http") ? src : `${SITE_URL}${src}`);

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
}) => {
  const location = useLocation();
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  const absoluteImage = toAbsolute(image);
  const jsonLdList = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:site_name" content="Dt. Mert Sakarya Dental Clinic" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {jsonLdList.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
